import React, {
  useMemo,
  useCallback,
  useRef,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { Col, Row, Form, FormGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  ExtraFieldsArea,
  ExtraFieldsForm,
} from '../../../components/ExtraFields';
import {
  ExtraFieldsFormId,
  ExtraFieldsSupportedObject,
} from '../../../components/ExtraFields/types';
import FlairButtonIcon from '../../../../../atomic/molecules/FlairButtonIcon';
import { FieldType, ExtraFieldInput } from '../../../__generated__/graphql';
import {
  getExtraFieldInputName,
  recordTypeFormKey,
} from '../../../components/ExtraFields/logic';
import { collectFiles } from '../../../../../utils/file';
import { fileSizeFormatter } from '../../../../../utils/file';
import { useToasts } from '../../../../../context/Toast';
import { Input } from '../../../../../components/form/Input';
import InputLabel from '../../../../../components/form/InputLabel';
import { CreatableDropdownControlled } from '../../../../../components/form/Selects/CreatableDropdownControlled';
import * as yup from 'yup';

type ExpenseItemState = {
  localId: string;
  recordTypeId: string;
  manualAmount?: number | null;
  merchantName?: string;
  merchantId?: string | null;
  extraFields?: ExtraFieldInput[];
  files?: File[];
};

type File = {
  fileName: string;
  fileBase64: string;
  size: number;
};

type Props = {
  recordTypes: Array<{ value: string; label: string }>;
  merchants: Array<{ value: string; label: string }>;
  onRemove?: () => void;
  onChange: (field: keyof ExpenseItemState, value: any) => void;
  onAddMerchant?: (value: string) => void;
  value: ExpenseItemState;
  index?: number;
  maxFileSize: number;
};

type FormValues = {
  [recordTypeFormKey]: string;
  manualAmount?: number | null;
  merchantName?: string;
  [key: string]: any;
};

export type ExpenseItemFormRef = {
  submitForm: () => Promise<void>;
  getFormData: () => Promise<{
    extraFields: ExtraFieldInput[];
  } | null>;
  hasErrors: () => boolean;
};

export const ExpenseItemForm = forwardRef<ExpenseItemFormRef, Props>(
  (
    {
      recordTypes,
      merchants,
      onRemove,
      onChange,
      onAddMerchant,
      value,
      index,
      maxFileSize,
    },
    ref,
  ) => {
    const formRef = useRef<HTMLDivElement>(null);
    const { t } = useTranslation();
    const { addError } = useToasts();

    useImperativeHandle(
      ref,
      () => ({
        submitForm: async () => {
          if (formRef.current) {
            const form = formRef.current.querySelector('form');
            if (form) {
              form.requestSubmit();
            }
          }
        },
        getFormData: async () => {
          if (formRef.current) {
            const form = formRef.current.querySelector('form');
            if (!form) return null;

            return {
              extraFields: value.extraFields || [],
            };
          }
          return null;
        },
        hasErrors: () => {
          if (!formRef.current) return false;
          return formRef.current.querySelectorAll('.is-invalid').length > 0;
        },
      }),
      [value.extraFields],
    );

    const handleSaveFieldValues = useCallback(
      (formData: FormValues, extraFields: ExtraFieldInput[]) => {
        const selectedRecordType = recordTypes.find(
          (rt) => rt.label === formData[recordTypeFormKey],
        );
        if (selectedRecordType) {
          onChange('recordTypeId', selectedRecordType.value);
        }
        const manualAmount = formData.manualAmount
          ? parseFloat(formData.manualAmount.toString())
          : null;
        onChange('manualAmount', manualAmount);

        if (formData.merchantName) {
          const selectedMerchant = merchants.find(
            (merchant) => merchant.value === formData.merchantName,
          );
          onChange('merchantName', formData.merchantName);
          onChange(
            'merchantId',
            selectedMerchant &&
              selectedMerchant.label !== selectedMerchant.value
              ? selectedMerchant.value
              : null,
          );
        }

        onChange('extraFields', extraFields);
      },
      [onChange, recordTypes, merchants],
    );

    const onFileChange = useCallback(
      async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.validity.valid || !event.target.files) {
          return;
        }

        try {
          const files = await collectFiles(event.target.files);
          const filesTotalSize = files.reduce(
            (totalSize, file) => totalSize + file.size,
            0,
          );

          if (filesTotalSize > maxFileSize) {
            addError(t('expenses.form.sizeLimitError'));
            event.target.value = '';
            return;
          }

          onChange('files', files);
        } catch (error) {
          addError(t('expenses.form.fileUploadError'));
          event.target.value = '';
        }
      },
      [addError, onChange, t, maxFileSize],
    );

    const contextParams = useMemo(
      () =>
        value.recordTypeId
          ? [
              {
                name: 'recordTypeId',
                type: FieldType.String,
                value: value.recordTypeId,
              },
            ]
          : [],
      [value.recordTypeId],
    );

    const defaultValues = useMemo(() => {
      const values =
        value.extraFields?.reduce((acc, field) => {
          const cleanFieldName = field.fieldApiName.replace(
            `expenseItem_${value.localId}_`,
            '',
          );
          const inputName = getExtraFieldInputName(
            cleanFieldName,
            field.type || FieldType.String,
          );
          let parsedValue = field.valueJson;
          try {
            parsedValue =
              field.valueJson != null ? JSON.parse(field.valueJson) : null;
          } catch {
            parsedValue = field.valueJson;
          }

          return {
            ...acc,
            [inputName]: parsedValue,
          };
        }, {} as FormValues) || {};

      const recordType = recordTypes.find(
        (rt) => rt.value === value.recordTypeId,
      );

      return {
        ...values,
        [recordTypeFormKey]: recordType?.label || recordTypes[0]?.label,
        manualAmount: value.manualAmount || null,
        merchantName: value.merchantName || '',
      };
    }, [
      value.extraFields,
      value.recordTypeId,
      value.manualAmount,
      value.merchantName,
      recordTypes,
      value.localId,
    ]);

    const itemFormValidationSchema = useMemo(() => {
      return yup.object().shape({
        [recordTypeFormKey]: yup
          .string()
          .required(t('validations.mixed.required')),
        merchantName: yup.string().required(t('validations.mixed.required')),
      });
    }, [t]);

    return (
      <>
        {index !== 0 && index !== undefined && (
          <div className="border-top mb-5 mt-5" />
        )}
        <div className="mb-3 position-relative" ref={formRef}>
          {onRemove && (
            <div
              className="position-absolute"
              style={{ top: '0rem', right: '0rem' }}>
              <FlairButtonIcon
                icon="close-outline"
                size="sm"
                variant="text"
                onClick={onRemove}
                className="text-danger"
              />
            </div>
          )}

          <ExtraFieldsForm<FormValues>
            objectApiName={ExtraFieldsSupportedObject.ExpenseItem}
            formId={ExtraFieldsFormId.ExpensesRequestExpenseItem}
            onSubmit={handleSaveFieldValues}
            validationSchema={itemFormValidationSchema}
            defaultValues={defaultValues}>
            <Row>
              <Col lg={12}>
                <Input
                  label={t('expenses.expenseSideBar.itemManualAmount')}
                  name="manualAmount"
                  type="number"
                  step="0.01"
                  size="md"
                />
              </Col>
              <Col lg={12} className="mb-4">
                <FormGroup>
                  <InputLabel
                    label={t('expenses.form.fields.merchantLabel')}
                    required={true}
                  />
                  <CreatableDropdownControlled
                    name="merchantName"
                    placeholder={t('expenses.form.fields.merchantPlaceholder')}
                    options={merchants}
                    onCreate={onAddMerchant || (() => {})}
                    heightMode="large"
                  />
                </FormGroup>
              </Col>
              <Col lg={12}>
                <ExtraFieldsArea
                  objectApiName={ExtraFieldsSupportedObject.ExpenseItem}
                  formId={ExtraFieldsFormId.ExpensesRequestExpenseItem}
                  contextParams={contextParams}
                />
              </Col>
              <Col lg={12}>
                <FormGroup>
                  <Form.Label>
                    {t('expenses.form.fields.fileLabelWithSize', {
                      size: fileSizeFormatter(maxFileSize),
                    })}
                  </Form.Label>
                  <Form.Control type="file" multiple onChange={onFileChange} />
                </FormGroup>
              </Col>
            </Row>
          </ExtraFieldsForm>
        </div>
      </>
    );
  },
);
